import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { Translate as TranslateIcon } from '@mui/icons-material';
import FieldMappingInput from '../targets/FieldMappingInput';
import DatasetValueMapDialog from './DatasetValueMapDialog';
import { emptyRule, isDatasetPath, primaryPath, ruleCountLabel } from '../../utils/datasetMappingUtils';
import type { RulePlace, SourceForm } from '../../utils/datasetMappingUtils';
import type { ColumnType } from '../../utils/datasetFormUtils';

interface DatasetColumnSourceProps {
  /** The column's label, such as "Column 2". */
  label: string;
  column: string;
  columnType: ColumnType;
  source: SourceForm;
  /** Saved in a saved input: the path is fixed, and a translation takes only new entries. */
  locked: boolean;
  /** A key field or the ordering field, and whether the dataset has a row path: what the rules may translate. */
  place: RulePlace;
  /** An optional column that is neither a key nor the ordering field may be left empty by an input. */
  mayBeNotProvided: boolean;
  rowSample: Record<string, unknown>;
  error?: string;
  disabled: boolean;
  onChange: (source: SourceForm) => void;
}

const acceptsMappingPath = (path: string) => isDatasetPath(path, true);

/** Where one column reads from in the selected input: a path, nothing, or translation rules. */
const DatasetColumnSource: React.FC<DatasetColumnSourceProps> = ({
  label,
  column,
  columnType,
  source,
  locked,
  place,
  mayBeNotProvided,
  rowSample,
  error,
  disabled,
  onChange,
}) => {
  const [translating, setTranslating] = useState(false);
  const fixed = disabled || locked;
  // A translation added to a saved plain path changes what saved rows read, so it is not offered.
  const translatable = source.kind === 'rules' || (source.kind === 'path' && !fixed);
  const rules = source.kind === 'rules' ? source.rules : [{ ...emptyRule(), path: primaryPath(source) }];

  return (
    <Box>
      {source.kind === 'path' && (
        <FieldMappingInput
          label={`${label} path`}
          value={source.path}
          onChange={(path) => onChange({ kind: 'path', path })}
          sampleEvent={rowSample}
          disabled={fixed}
          error={error}
          pathFilter={acceptsMappingPath}
        />
      )}
      {source.kind === 'none' && (
        <Typography variant="body2" color="text.secondary">This input does not provide it; its rows leave it empty.</Typography>
      )}
      {source.kind === 'rules' && (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {source.rules.map((r) => r.path).join(', then ')}
        </Typography>
      )}
      {source.kind !== 'path' && error && <Typography variant="caption" color="error">{error}</Typography>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        {(mayBeNotProvided || source.kind === 'none') && (
          <FormControlLabel
            label={<Typography variant="body2">Not provided</Typography>}
            control={(
              <Checkbox
                size="small"
                checked={source.kind === 'none'}
                onChange={(e) => onChange(e.target.checked ? { kind: 'none' } : { kind: 'path', path: '' })}
                disabled={fixed}
                inputProps={{ 'aria-label': `${label} not provided` }}
              />
            )}
          />
        )}
        {translatable && (
          <Button size="small" startIcon={<TranslateIcon />} onClick={() => setTranslating(true)} disabled={disabled}>
            {ruleCountLabel(source)}
          </Button>
        )}
      </Box>

      {translating && (
        <DatasetValueMapDialog
          column={column}
          columnType={columnType}
          place={place}
          rules={rules}
          rowSample={rowSample}
          onApply={(next) => {
            onChange({ kind: 'rules', rules: next });
            setTranslating(false);
          }}
          onUsePath={source.kind === 'rules' ? () => {
            onChange({ kind: 'path', path: primaryPath(source) });
            setTranslating(false);
          } : undefined}
          onClose={() => setTranslating(false)}
        />
      )}
    </Box>
  );
};

export default DatasetColumnSource;
